import { FormProvider, useTnForm } from '@thinknimble/tn-forms-react'
import { FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from 'src/components/button'
import { ErrorMessage } from 'src/components/errors'
import { Input } from 'src/components/input'
import {
  SurveyPinEntryForm,
  SurveyPinEntryFormInputs,
  TSurveyPinEntryForm,
} from 'src/services/survey/forms'

type PinEntryFormProps = {
  onSubmit: (pin: string) => void
  isPending: boolean
  error: string | null
}

const PinEntryFormInner = ({ onSubmit, isPending, error }: PinEntryFormProps) => {
  const { t } = useTranslation()
  const { form, createFormFieldChangeHandler } = useTnForm<TSurveyPinEntryForm>()

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    form.validate()
    if (!form.isValid) return
    onSubmit(form.pin.value ?? '')
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col gap-4"
      data-testid="pin-entry-form"
      noValidate
    >
      <div>
        <h2 className="text-lg font-medium text-text-primary">{t('surveyPin.entryTitle')}</h2>
        <p className="mt-1 text-sm text-text-secondary">{t('surveyPin.entryDescription')}</p>
      </div>
      <Input
        type="password"
        inputMode="numeric"
        autoComplete="off"
        label={t('surveyPin.entryLabel')}
        value={form.pin.value ?? ''}
        onChange={(e) => createFormFieldChangeHandler(form.pin)(e.target.value)}
        data-testid="pin-entry-input"
      />
      {error && <ErrorMessage>{error}</ErrorMessage>}
      <Button
        type="submit"
        isLoading={isPending}
        disabled={isPending || !form.isValid}
        data-testid="pin-entry-submit"
      >
        {t('surveyPin.entrySubmit')}
      </Button>
    </form>
  )
}

/** Asks the respondent for the PIN they chose, to reopen their survey. */
export const PinEntryForm = (props: PinEntryFormProps) => (
  <FormProvider<SurveyPinEntryFormInputs> formClass={SurveyPinEntryForm}>
    <PinEntryFormInner {...props} />
  </FormProvider>
)
